import { applyGoogleTranslateDOMPatch } from '@/utils/applyGoogleTranslateDOMPatch';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { Provider, ProviderType, TrackingEvent } from '../types';
import { flushLocaleAndGoogleTransCookie } from '@/utils/locale-cookie';

interface GoogleTranslateConfig {
  scriptUrl?: string;
  isEnabled?: boolean;
}

export class GoogleTranslateProvider implements Provider {
  type = ProviderType.GOOGLE_TRANSLATE;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: GoogleTranslateConfig;

  constructor(config: GoogleTranslateConfig = {}) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    // 默认在本地环境启用日志
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // apply once
      applyGoogleTranslateDOMPatch();
      flushLocaleAndGoogleTransCookie();

      // 加载Google翻译脚本
      // 这种track为空的provider，不需要等待加载完成，直接加载即可，返回init初始化完成，不影响后面的功能
      // 为了减小对TBT的影响，把其加载的时序再往后延
      setTimeout(() => {
        this.loadGoogleTranslateScript();
      }, 5000);

      this.initialized = true;
      return true;
    } catch (error) {
      logger.error('Failed to initialize Google Translate:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    // Google Translate主要用于页面翻译，不需要track特定事件
    if (this.loggingEnabled) {
      logger.log(
        `Google Translate: Track method called - Google Translate handles translation automatically, ${JSON.stringify(
          event,
        )}`,
      );
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  enableLog(): void {
    this.loggingEnabled = true;
  }

  disableLog(): void {
    this.loggingEnabled = false;
  }

  /**
   * 加载Google翻译脚本
   */
  private async loadGoogleTranslateScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const scriptUrl =
          this.config.scriptUrl || 'https://common.xmslol.com/translate/google.translate.js';

        const script = document.createElement('script');
        script.async = true;
        script.src = scriptUrl;

        script.onload = () => {
          if (this.loggingEnabled) {
            logger.log('Google Translate: Script loaded successfully');
          }

          // 设置翻译组件加载完成的处理逻辑
          this.setupTranslateLoadedHandler();
          resolve();
        };

        script.onerror = () => {
          const error = 'Failed to load Google Translate script';
          logger.error('Google Translate:', error);
          reject(new Error(error));
        };

        document.body.appendChild(script);

        if (this.loggingEnabled) {
          logger.log('Google Translate: Script element added to body');
        }
      } catch (error) {
        logger.error('Google Translate: Error creating script element:', error);
        reject(error);
      }
    });
  }

  /**
   * 设置翻译组件加载完成的处理逻辑
   */
  private setupTranslateLoadedHandler(): void {
    if (this.loggingEnabled) {
      logger.log('Google Translate: Setting up translate loaded handler');
    }

    // 创建自定义事件
    const googleTranslateLoaded = new CustomEvent('googleTranslateLoaded');

    const interval = setInterval(() => {
      const selectElement = document.querySelector('.goog-te-combo');

      if (selectElement) {
        clearInterval(interval);

        if (this.loggingEnabled) {
          logger.log('Google Translate: Translation component loaded successfully');
        }

        // 触发自定义事件
        const translateElement = document.getElementById('google_translate_element');
        if (translateElement) {
          translateElement.dispatchEvent(googleTranslateLoaded);

          if (this.loggingEnabled) {
            logger.log('Google Translate: Custom event dispatched');
          }
        }

        // 隐藏Google翻译添加的所有元素
        this.hideGoogleTranslateElements();
      }
    }, 1000);
  }

  /**
   * 隐藏Google翻译添加的所有元素
   */
  private hideGoogleTranslateElements(): void {
    try {
      // 隐藏网页翻译添加的所有元素，前缀是"VIpgJd-"
      const elements = document.querySelectorAll('[class^="VIpgJd-"]');
      elements.forEach((item) => {
        (item as HTMLElement).style.display = 'none';
      });

      if (this.loggingEnabled && elements.length > 0) {
        logger.log(`Google Translate: Hidden ${elements.length} Google Translate UI elements`);
      }
    } catch (error) {
      logger.error('Google Translate: Error hiding translate elements:', error);
    }
  }
}
